import React, { useEffect, useRef, useState } from 'react';
import { useSyncTracking } from '../context/SyncTrackingContext';
import { useLocationContext } from '../context/LocationContext';
import { SyncTrackingDrawer } from '../components/sync-tracking/SyncTrackingDrawer';
import { AIBriefPanel } from '../components/sync-tracking/AIBriefPanel';
import { DateRangeCalendar } from '../components/sync-tracking/DateRangeCalendar';
import { CopyableId } from '../components/shared/CopyableId';
import { Pagination } from '../components/mapping/Pagination';
import { syncTrackingApi, EngineUnavailableError, type SyncBriefResponse, type SyncTrackingItem } from '../api/syncTrackingApi';
import { Toast } from '../components/shared/Toast';
import {
    statusView, dirView, stageLabel, stagePercent, stageBarClass,
    recordSub, recordRef, entityTypeLabel, isAppointmentEntity, sumByGroup,
    attemptTextClass, timeAgo, type StatusGroup,
} from '../lib/syncTrackingMeta';

const PAGE_SIZE = 20;
const MAX_DATE_SPAN_DAYS = 30;

type EntityFilter = 'all' | 'patient' | 'appointment';
type DirFilter = 'all' | 'emr_to_ghl' | 'ghl_to_emr';
type GroupFilter = 'all' | StatusGroup;

interface Filters {
    q: string;
    entity: EntityFilter;
    direction: DirFilter;
    status: string;
    trigger: string;
    group: GroupFilter;
    dateFrom: string;
    dateTo: string;
}

const EMPTY_FILTERS: Filters = { q: '', entity: 'all', direction: 'all', status: 'all', trigger: 'all', group: 'all', dateFrom: '', dateTo: '' };

/** Whole-day span between two `YYYY-MM-DD` strings, compared at UTC midnight to avoid local-timezone drift. */
function daySpan(from: string, to: string): number {
    const a = new Date(`${from}T00:00:00Z`).getTime();
    const b = new Date(`${to}T00:00:00Z`).getTime();
    return Math.round((b - a) / 86400000);
}

function formatShortDate(iso: string): string {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

const STATUS_OPTIONS: [string, string][] = [
    ['all', 'All statuses'], ['queued', 'Queued'], ['processing', 'Processing'],
    ['waiting_dependency', 'Waiting on dependency'], ['confirmation_pending', 'Awaiting confirmation'],
    ['succeeded', 'Synced'], ['ignored', 'Skipped'], ['blocked', 'Blocked'],
    ['retryable_failed', 'Retrying'], ['dead_letter', 'Failed'], ['cancelled', 'Cancelled'],
    ['acknowledged', 'Acknowledged'],
];
const TRIGGER_OPTIONS: [string, string][] = [
    ['all', 'All triggers'], ['scheduled_poll', 'Scheduled'], ['webhook', 'Webhook'],
    ['manual', 'Manual'], ['backfill', 'Backfill'],
];

interface TileDef {
    key: StatusGroup;
    label: string;
    sub: string;
    dot: string;
    num: string;
    activeCls: string;
}
const TILES: TileDef[] = [
    { key: 'done', label: 'Completed', sub: 'Synced + skipped', dot: 'bg-mapper-green-dot', num: 'text-mapper-teal-dark', activeCls: 'border-[#C9E9DC] shadow-[0_0_0_3px_#C9E9DC55]' },
    { key: 'active', label: 'In progress', sub: 'Queued + running', dot: 'bg-mapper-blue', num: 'text-mapper-blue', activeCls: 'border-[#CDD9F0] shadow-[0_0_0_3px_#CDD9F055]' },
    { key: 'attention', label: 'Needs attention', sub: 'Blocked + retrying', dot: 'bg-mapper-amber-bar', num: 'text-mapper-amber', activeCls: 'border-[#F0DFB8] shadow-[0_0_0_3px_#F0DFB855]' },
    { key: 'failed', label: 'Failed', sub: 'Dead-letter + cancelled', dot: 'bg-mapper-red-bar', num: 'text-mapper-red-dark', activeCls: 'border-[#F0C6CE] shadow-[0_0_0_3px_#F0C6CE55]' },
    { key: 'acknowledged', label: 'Acknowledged', sub: 'Reviewed by operator', dot: 'bg-mapper-muted-3', num: 'text-mapper-body-text', activeCls: 'border-mapper-border-2 shadow-[0_0_0_3px_#E2E6EB55]' },
];

const GRID = 'grid-cols-[2.4fr_132px_150px_1.3fr_66px_100px_22px]';

const SearchIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#A6AEBA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-1/2 -translate-y-1/2">
        <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
    </svg>
);
const ChevronDown = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
);
const CheckIcon = (hidden: boolean) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={hidden ? { visibility: 'hidden' } : undefined}><path d="M20 6 9 17l-5-5" /></svg>
);
const RowArrow = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#B0B8C2" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);
const CalendarIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></svg>
);
const BriefSparkle = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /><path d="M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
);

function findLabel(opts: [string, string][], value: string): string {
    return opts.find(o => o[0] === value)?.[1] ?? '';
}

export const SyncTrackingPage: React.FC = () => {
    const { counts, engineUnavailable, reload } = useSyncTracking();
    const { currentLocation, locationId } = useLocationContext();
    const emr = currentLocation?.emr ?? '';

    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    const [debouncedQ, setDebouncedQ] = useState('');
    const [page, setPage] = useState(1);
    const [menuOpen, setMenuOpen] = useState<'status' | 'trigger' | 'date' | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [listReloadKey, setListReloadKey] = useState(0);
    const [draftDateFrom, setDraftDateFrom] = useState('');
    const [draftDateTo, setDraftDateTo] = useState('');

    const [items, setItems] = useState<SyncTrackingItem[]>([]);
    const [total, setTotal] = useState(0);
    const [rowsLoading, setRowsLoading] = useState(false);
    const [rowsError, setRowsError] = useState<string | null>(null);

    const [briefOpen, setBriefOpen] = useState(false);
    const [brief, setBrief] = useState<SyncBriefResponse | null>(null);
    const [briefLoading, setBriefLoading] = useState(false);
    const [briefError, setBriefError] = useState<string | null>(null);
    const [reportSending, setReportSending] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const filterMenusRef = useRef<HTMLDivElement>(null);

    const setF = <K extends keyof Filters>(k: K, v: Filters[K]) => {
        setFilters(f => ({ ...f, [k]: v }));
        setMenuOpen(null);
    };

    // Close any open filter dropdown (status / trigger / date) on outside click or Escape.
    useEffect(() => {
        if (!menuOpen) return;
        const onPointerDown = (e: MouseEvent) => {
            if (filterMenusRef.current && !filterMenusRef.current.contains(e.target as Node)) {
                setMenuOpen(null);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(null);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    // Debounce free-text search before it drives a server request.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(filters.q), 400);
        return () => clearTimeout(t);
    }, [filters.q]);

    // Reset to page 1 whenever a filter changes.
    useEffect(() => {
        setPage(1);
    }, [debouncedQ, filters.entity, filters.direction, filters.status, filters.trigger, filters.group, filters.dateFrom, filters.dateTo]);

    useEffect(() => {
        if (!locationId || !emr) { setItems([]); setTotal(0); return; }
        let cancelled = false;
        setRowsLoading(true);
        setRowsError(null);

        syncTrackingApi.list(locationId, emr, {
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
            q: debouncedQ || undefined,
            entity: filters.entity !== 'all' ? filters.entity : undefined,
            direction: filters.direction !== 'all' ? filters.direction : undefined,
            status: filters.status !== 'all' ? filters.status : undefined,
            trigger: filters.trigger !== 'all' ? filters.trigger : undefined,
            group: filters.group !== 'all' ? filters.group : undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
        })
            .then(res => {
                if (cancelled) return;
                setItems(res.items);
                setTotal(res.total);
            })
            .catch(err => {
                if (cancelled) return;
                setItems([]);
                setTotal(0);
                if (!(err instanceof EngineUnavailableError)) {
                    setRowsError(err instanceof Error ? err.message : 'Failed to load sync tracking');
                }
            })
            .finally(() => { if (!cancelled) setRowsLoading(false); });

        return () => { cancelled = true; };
    }, [locationId, emr, page, debouncedQ, filters.entity, filters.direction, filters.status, filters.trigger, filters.group, filters.dateFrom, filters.dateTo, listReloadKey]);

    // A brief describes one practice's failures, so it can't carry across a
    // location switch.
    useEffect(() => {
        setBriefOpen(false);
        setBrief(null);
        setBriefError(null);
    }, [locationId, emr]);

    const handleAcknowledged = () => {
        setSelectedId(null);
        setListReloadKey(k => k + 1);
        reload();
    };

    const runBrief = async () => {
        if (!locationId || !emr) return;
        setBriefLoading(true);
        setBriefError(null);
        try {
            setBrief(await syncTrackingApi.aiBrief(locationId, emr));
        } catch (err) {
            setBrief(null);
            setBriefError(
                err instanceof EngineUnavailableError
                    ? 'Sync tracking is unavailable — the engine database is not configured.'
                    : err instanceof Error ? err.message : 'Failed to generate AI brief',
            );
        } finally {
            setBriefLoading(false);
        }
    };

    // Reuses an already-generated brief when reopening; "Re-analyse" inside the
    // panel is what forces a fresh Gemini call.
    const toggleBrief = () => {
        if (briefOpen) { setBriefOpen(false); return; }
        setBriefOpen(true);
        if (!brief && !briefLoading) runBrief();
    };

    /** Fleet-wide Sync Findings PDF → Slack (same as the daily 11:00 IST cron). */
    const pushFindingsReport = async () => {
        if (reportSending) return;
        setReportSending(true);
        try {
            const result = await syncTrackingApi.findingsReport();
            setToast({
                message: `Sync Findings sent to Slack (last 24h) — ${result.practices} practices, ${result.open_issues} open issues`,
                type: 'success',
            });
        } catch (err) {
            setToast({
                message: err instanceof EngineUnavailableError
                    ? 'Engine DB is not configured — cannot generate the report.'
                    : err instanceof Error ? err.message : 'Failed to send Sync Findings report',
                type: 'error',
            });
        } finally {
            setReportSending(false);
        }
    };

    const tileCount = (g: StatusGroup) => sumByGroup(counts, g);
    const totalForLocation = Object.values(counts).reduce((a, b) => a + b, 0);
    // What the AI brief covers: everything under the Needs attention + Failed tiles.
    const attentionTotal = tileCount('attention') + tileCount('failed');

    const anyFilter = !!(filters.q || filters.entity !== 'all' || filters.direction !== 'all' ||
        filters.status !== 'all' || filters.trigger !== 'all' || filters.group !== 'all' ||
        filters.dateFrom || filters.dateTo);

    const clearFilters = () => { setFilters(EMPTY_FILTERS); setMenuOpen(null); setDraftDateFrom(''); setDraftDateTo(''); };
    const selected = selectedId != null ? items.find(i => i.id === selectedId) ?? null : null;

    const openDateMenu = () => {
        setDraftDateFrom(filters.dateFrom);
        setDraftDateTo(filters.dateTo);
        setMenuOpen(m => m === 'date' ? null : 'date');
    };
    const dateRangeError = draftDateFrom && draftDateTo
        ? (draftDateFrom > draftDateTo
            ? 'Start date must be before end date'
            : daySpan(draftDateFrom, draftDateTo) > MAX_DATE_SPAN_DAYS
                ? `Range can't exceed ${MAX_DATE_SPAN_DAYS} days`
                : null)
        : null;
    const applyDateRange = () => {
        if (dateRangeError) return;
        setFilters(f => ({ ...f, dateFrom: draftDateFrom, dateTo: draftDateTo }));
        setMenuOpen(null);
    };
    const clearDateRange = () => {
        setDraftDateFrom(''); setDraftDateTo('');
        setFilters(f => ({ ...f, dateFrom: '', dateTo: '' }));
        setMenuOpen(null);
    };
    const dateRangeLabel = filters.dateFrom || filters.dateTo
        ? `${filters.dateFrom ? formatShortDate(filters.dateFrom) : 'Start'} – ${filters.dateTo ? formatShortDate(filters.dateTo) : 'Today'}`
        : 'Date range';

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(page * PAGE_SIZE, total);
    const summary = anyFilter ? `Showing ${rangeStart}-${rangeEnd} of ${total}` : `${total} records`;

    const segClass = (active: boolean) =>
        `px-[13px] py-[6px] rounded-lg text-[13px] transition-colors ${active ? 'bg-white text-mapper-teal-hover shadow-mapper-segmented font-semibold' : 'text-mapper-muted font-medium'}`;

    const dropdownBtnClass = (active: boolean) =>
        `flex items-center gap-[7px] px-3 py-2 border rounded-[10px] text-[13px] font-semibold ${active ? 'border-mapper-teal text-mapper-teal-hover bg-[#F2FAF8]' : 'border-mapper-border-2 text-mapper-body-text bg-white'}`;

    const renderMenu = (opts: [string, string][], current: string, col: 'status' | 'trigger') => (
        <div className="absolute top-[calc(100%+6px)] left-0 min-w-[210px] bg-white border border-mapper-border-5 rounded-[10px] shadow-mapper-dropdown p-[5px] z-50">
            {opts.map(([val, label]) => (
                <button
                    key={val}
                    onClick={() => setF(col, val)}
                    className={`flex items-center gap-2 w-full px-[10px] py-2 rounded-[7px] text-[13px] text-left hover:bg-[#F5F7F9] ${current === val ? 'bg-[#EAF5F2] text-mapper-teal-hover font-semibold' : 'text-mapper-body-text'}`}
                >
                    {CheckIcon(current !== val)}
                    <span>{label}</span>
                </button>
            ))}
        </div>
    );

    return (
        <div className="mapper-ui font-sans text-mapper-text">
            {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
            <div className="max-w-[1240px] mx-auto px-10 pt-[30px] pb-[70px]">
                {/* header */}
                <div className="flex items-end justify-between mb-[22px] gap-5">
                    <div>
                        <h1 className="text-[24px] font-extrabold tracking-[-0.5px] mb-[5px]">Sync Tracking</h1>
                        <p className="text-[14px] text-mapper-muted max-w-[640px]">
                            Every patient and appointment record moving between your EMR and Advital — with its live status, stage, and full event history. Click any record to trace it end to end.
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-[9px] shrink-0">
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={pushFindingsReport}
                                disabled={reportSending || engineUnavailable}
                                title="Generate the all-active-practices Sync Findings PDF (last 24h) and post it to Slack — same as the daily 11:00 IST cron"
                                className="flex items-center gap-2 py-[9px] px-4 rounded-[10px] text-[13.5px] font-semibold whitespace-nowrap border border-mapper-border-2 bg-white text-mapper-body-text disabled:opacity-50 hover:border-mapper-teal hover:text-mapper-teal-hover"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <path d="M14 2v6h6" /><path d="M8 13h8M8 17h5" />
                                </svg>
                                <span>{reportSending ? 'Sending report…' : 'Send Sync Findings'}</span>
                            </button>
                            <button
                                onClick={toggleBrief}
                                disabled={!locationId || !emr || engineUnavailable}
                                title="Have AI analyse this practice's most common sync errors"
                                className={`flex items-center gap-2 py-[9px] px-4 rounded-[10px] text-[13.5px] font-semibold whitespace-nowrap disabled:opacity-50 ${
                                    briefOpen
                                        ? 'bg-mapper-teal-bg-3 text-mapper-teal-hover border border-mapper-teal-border-3'
                                        : 'bg-mapper-teal text-white shadow-mapper-btn'
                                }`}
                            >
                                {BriefSparkle}
                                <span>{briefLoading ? 'Analysing…' : briefOpen ? 'Hide AI Brief' : 'AI Brief'}</span>
                                {!briefOpen && attentionTotal > 0 && (
                                    <span className="font-mono text-[11.5px] font-bold px-[6px] py-[1px] rounded-full bg-white/20">
                                        {attentionTotal}
                                    </span>
                                )}
                            </button>
                        </div>
                        <span className="text-[12.5px] text-mapper-muted-2 whitespace-nowrap">{summary}</span>
                    </div>
                </div>

                {engineUnavailable && (
                    <div className="border border-[#F0DFB8] bg-[#FFFDF7] rounded-[12px] px-4 py-3 mb-[22px] text-[13px] text-[#7A5A15]">
                        Sync tracking is unavailable — the engine database is not configured for this environment.
                    </div>
                )}
                {currentLocation && !currentLocation.sync_active && (
                    <div className="border border-[#F0DFB8] bg-[#FFFDF7] rounded-[12px] px-4 py-3 mb-[22px] text-[13px] text-[#7A5A15]">
                        Sync is inactive for <strong className="font-bold">{currentLocation.name}</strong> — new EMR/Advital records for this practice won't be picked up until sync is re-enabled.
                    </div>
                )}
                {rowsError && (
                    <div className="border border-mapper-red-border bg-mapper-red-bg-2 rounded-[12px] px-4 py-3 mb-[22px] text-[13px] text-mapper-red">{rowsError}</div>
                )}

                {/* summary tiles */}
                <div className="grid grid-cols-5 gap-[14px] mb-[22px]">
                    {TILES.map(t => {
                        const active = filters.group === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setF('group', active ? 'all' : t.key)}
                                className={`text-left bg-mapper-white border-[1.5px] rounded-[14px] px-[18px] py-4 transition-shadow ${active ? t.activeCls : 'border-mapper-border'}`}
                            >
                                <div className="flex items-center gap-2 mb-[10px]">
                                    <span className={`w-[9px] h-[9px] rounded-full ${t.dot}`} />
                                    <span className="text-[13px] font-semibold text-mapper-body-text">{t.label}</span>
                                </div>
                                <div className={`font-mono text-[28px] font-bold tracking-[-1px] leading-none ${t.num}`}>{tileCount(t.key)}</div>
                                <div className="text-[11.5px] text-mapper-muted-3 mt-[5px]">{t.sub}</div>
                            </button>
                        );
                    })}
                </div>

                {/* AI brief — sits under the tiles: they say how many, this says why */}
                {briefOpen && (
                    <AIBriefPanel
                        brief={brief}
                        loading={briefLoading}
                        error={briefError}
                        onRefresh={runBrief}
                        onClose={() => setBriefOpen(false)}
                    />
                )}

                {/*
                  * Filter bar, in two explicit rows: search + what-am-I-looking-at
                  * scope on top, then the narrowing dropdowns. Deliberately not one
                  * wrapping row — at container width the six controls overflowed by a
                  * hair, wrapping the date-range button to the left edge where its
                  * right-anchored menu opened out past the page and under the sidebar.
                  * Fixed rows keep every menu's anchor predictable.
                  */}
                <div className="flex flex-col gap-[10px] mb-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[220px]">
                            {SearchIcon}
                            <input
                                value={filters.q}
                                onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                                placeholder="Search by name, EMR/Advital ID, patient or appointment ID…"
                                className="w-full box-border pl-[34px] pr-3 py-[9px] border border-mapper-border-2 rounded-[10px] text-[13.5px] text-mapper-body-text outline-none bg-white"
                            />
                        </div>
                        <div className="flex gap-[3px] bg-mapper-gray-bg rounded-[10px] p-[3px]">
                            {([['all', 'All'], ['patient', 'Patients'], ['appointment', 'Appointments']] as [EntityFilter, string][]).map(([val, label]) => (
                                <button key={val} onClick={() => setF('entity', val)} className={segClass(filters.entity === val)}>{label}</button>
                            ))}
                        </div>
                        <div className="flex gap-[3px] bg-mapper-gray-bg rounded-[10px] p-[3px]">
                            {([['all', 'All'], ['emr_to_ghl', 'EMR → Advital'], ['ghl_to_emr', 'Advital → EMR']] as [DirFilter, string][]).map(([val, label]) => (
                                <button key={val} onClick={() => setF('direction', val)} className={segClass(filters.direction === val)}>{label}</button>
                            ))}
                        </div>
                    </div>
                    <div ref={filterMenusRef} className="flex items-center gap-3 flex-wrap">
                        <div className="relative">
                            <button onClick={() => setMenuOpen(m => m === 'status' ? null : 'status')} className={dropdownBtnClass(filters.status !== 'all')}>
                                {findLabel(STATUS_OPTIONS, filters.status)}
                                {ChevronDown}
                            </button>
                            {menuOpen === 'status' && renderMenu(STATUS_OPTIONS, filters.status, 'status')}
                        </div>
                        <div className="relative">
                            <button onClick={() => setMenuOpen(m => m === 'trigger' ? null : 'trigger')} className={dropdownBtnClass(filters.trigger !== 'all')}>
                                {findLabel(TRIGGER_OPTIONS, filters.trigger)}
                                {ChevronDown}
                            </button>
                            {menuOpen === 'trigger' && (
                                <div className="absolute top-[calc(100%+6px)] left-0 min-w-[170px] bg-white border border-mapper-border-5 rounded-[10px] shadow-mapper-dropdown p-[5px] z-50">
                                    {TRIGGER_OPTIONS.map(([val, label]) => (
                                        <button key={val} onClick={() => setF('trigger', val)} className={`flex items-center gap-2 w-full px-[10px] py-2 rounded-[7px] text-[13px] text-left hover:bg-[#F5F7F9] ${filters.trigger === val ? 'bg-[#EAF5F2] text-mapper-teal-hover font-semibold' : 'text-mapper-body-text'}`}>
                                            {CheckIcon(filters.trigger !== val)}
                                            <span>{label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="relative">
                            <button onClick={openDateMenu} className={dropdownBtnClass(!!(filters.dateFrom || filters.dateTo))}>
                                {CalendarIcon}
                                {dateRangeLabel}
                                {ChevronDown}
                            </button>
                            {menuOpen === 'date' && (
                                <DateRangeCalendar
                                    from={draftDateFrom}
                                    to={draftDateTo}
                                    error={dateRangeError}
                                    onFromChange={setDraftDateFrom}
                                    onToChange={setDraftDateTo}
                                    onClear={clearDateRange}
                                    onApply={applyDateRange}
                                />
                            )}
                        </div>
                        {anyFilter && (
                            <button onClick={clearFilters} className="flex items-center gap-[5px] ml-auto px-3 py-2 text-mapper-teal-hover text-[13px] font-semibold">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* table */}
                <div className="bg-mapper-white border border-mapper-border rounded-[15px] overflow-hidden">
                    <div className={`grid ${GRID} items-center gap-[14px] px-[22px] py-3 border-b border-mapper-border-3 bg-mapper-surface text-[11px] font-bold tracking-[0.4px] text-mapper-muted-3`}>
                        <div>RECORD</div>
                        <div>DIRECTION</div>
                        <div>STATUS</div>
                        <div>STAGE</div>
                        <div className="text-center">TRIES</div>
                        <div>UPDATED</div>
                        <div></div>
                    </div>

                    {rowsLoading ? (
                        <div className="flex items-center justify-center py-[52px] text-[14px] text-mapper-muted-2">Loading sync records…</div>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 py-[52px] px-[22px] text-center">
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#C2C9D1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
                            <div className="text-[14px] font-semibold text-mapper-body-text-2">
                                {totalForLocation === 0 ? 'No sync records for this location yet' : 'No sync records match your filters'}
                            </div>
                            {anyFilter && (
                                <button onClick={clearFilters} className="flex items-center gap-[6px] px-[15px] py-2 rounded-[9px] border border-mapper-teal-border bg-mapper-teal-bg text-mapper-teal-hover text-[13px] font-semibold">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                    Clear filters
                                </button>
                            )}
                        </div>
                    ) : (
                        items.map(i => <TrackingRow key={i.id} item={i} selected={selectedId === i.id} onSelect={() => setSelectedId(i.id)} />)
                    )}

                    <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
                </div>
            </div>

            {selected && (
                <SyncTrackingDrawer
                    item={selected}
                    locationId={locationId!}
                    emr={emr}
                    onClose={() => setSelectedId(null)}
                    onAcknowledged={handleAcknowledged}
                />
            )}
        </div>
    );
};

const TrackingRow: React.FC<{ item: SyncTrackingItem; selected: boolean; onSelect: () => void }> = ({ item, selected, onSelect }) => {
    const sv = statusView(item.status);
    const dv = dirView(item.direction);
    const isAppt = isAppointmentEntity(item.entity_type);

    return (
        <button
            onClick={onSelect}
            className={`grid ${GRID} items-center gap-[14px] w-full text-left px-[22px] py-[14px] border-b border-mapper-border-4 transition-colors hover:bg-mapper-surface ${selected ? 'bg-mapper-teal-bg-2' : ''}`}
        >
            {/* record */}
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-[34px] h-[34px] rounded-[9px] shrink-0 flex items-center justify-center bg-mapper-border-4">
                    {isAppt ? (
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></svg>
                    ) : (
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.4" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></svg>
                    )}
                </div>
                <div className="min-w-0">
                    <div className="text-[14px] font-bold truncate">
                        {entityTypeLabel(item)} · <CopyableId value={recordRef(item)} />
                    </div>
                    <div className="text-[12px] text-mapper-muted-2 truncate">{recordSub(item)}</div>
                </div>
            </div>
            {/* direction */}
            <div className="flex items-center gap-[5px]">
                <span className={`text-[10.5px] font-bold px-[7px] py-[2px] rounded-md ${dv.fromPill}`}>{dv.from}</span>
                {RowArrow}
                <span className={`text-[10.5px] font-bold px-[7px] py-[2px] rounded-md ${dv.toPill}`}>{dv.to}</span>
            </div>
            {/* status */}
            <div>
                <span className={`inline-flex items-center gap-[6px] text-[12px] font-bold px-[10px] py-[3px] rounded-full ${sv.pill}`}>
                    <span className={`w-[7px] h-[7px] rounded-full ${sv.dot}`} style={sv.pulse ? { animation: 'mapperPulse 1.4s ease-in-out infinite' } : undefined} />
                    {sv.label}
                </span>
            </div>
            {/* stage */}
            <div className="min-w-0 pr-2">
                <div className="text-[12px] text-mapper-body-text-2 mb-1 truncate">{stageLabel(item.stage)}</div>
                <div className="h-1 bg-mapper-gray-bg rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${stageBarClass(item.status)}`} style={{ width: `${stagePercent(item.stage)}%` }} />
                </div>
            </div>
            {/* tries */}
            <div className={`text-center font-mono text-[12.5px] ${attemptTextClass(item.attempt_count, item.max_attempts)}`}>
                {item.attempt_count} / {item.max_attempts}
            </div>
            {/* updated */}
            <div className="text-[12px] text-mapper-muted-2">{timeAgo(item.updated_at)}</div>
            {/* chevron */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C2C9D1" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
        </button>
    );
};
